import sentry from '../../../sentry';
import { useContext, useRef } from 'react';
import { Button, Box, useToast } from '@chakra-ui/react';
import CustomToast from '../../../common/CustomToast';
import { getErrorResponsePayload } from '../../../utils/ajax';
import { MeContext } from '../../../context/MeContext';
import { useAppSelector } from '../../../store/redux/hooks';
import { selectFilterValues } from '../../../store/redux/slices/passList.slice';
import type { MeContextState } from '../../../store/types/account';
import { useExportPassesMutation } from '../../../store/redux/api/passApi';

function exportPassesErrorPayload(error: unknown) {
  if (
    error &&
    typeof error === 'object' &&
    'status' in error &&
    'data' in error
  ) {
    const e = error as {
      status?: number;
      data?: { message?: string; code?: number };
    };
    return {
      code: e.data?.code ?? e.status ?? null,
      message: e.data?.message ?? null,
    };
  }
  return getErrorResponsePayload(error);
}

const PassesListDownload = () => {
  const filterValues = useAppSelector(selectFilterValues);
  const [exportPasses, { isLoading: isExporting }] = useExportPassesMutation();
  const meCtx = useContext(MeContext) as { state: MeContextState };
  const { filterEntity } = meCtx.state;
  const toast = useToast();
  const ref = useRef<HTMLDivElement>(null);

  const generatePassesListData = async () => {
    const email = meCtx.state.account?.email?.trim();
    if (!email) {
      toast({
        render: (props) => (
          <CustomToast
            status="error"
            title="Missing email"
            description="We could not find an email for your account."
            onClose={props.onClose}
          />
        ),
      });
      return;
    }

    try {
      await exportPasses({
        entityId: filterEntity?.uuid,
        email,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        filters: filterValues,
      }).unwrap();

      toast({
        render: ({ onClose }) => (
          <CustomToast
            status="info"
            title="Please check your email"
            description={`A download link has been sent to ${email}. It may take a few minutes to arrive.`}
            onClose={onClose}
          />
        ),
      });
    } catch (error) {
      sentry.sendError(error);
      console.log(error);
      const { message, code } = exportPassesErrorPayload(error);
      code !== 401 &&
        toast({
          render: (props) => (
            <CustomToast
              status="error"
              title={message ? message : `Something went wrong`}
              description={!message && 'Please try again later'}
              onClose={props.onClose}
            />
          ),
        });
    }
  };

  return (
    <Box ref={ref}>
      <Button
        variant="outline"
        size="sm"
        px="17px"
        py="13px"
        borderRadius="5px"
        border="1.5px solid"
        borderColor="#3A4A57"
        bg="white"
        color="#3A4A57"
        textStyle="bodyFamilyMedium"
        fontSize="13px"
        _hover={{ bg: '#F8F8F8' }}
        onClick={() => {
          void generatePassesListData();
        }}
        isLoading={isExporting}
      >
        Download CSV
      </Button>
    </Box>
  );
};

export default PassesListDownload;
